/* Logo Enrichment — Run Detail (Iter 39 Phase 3 + 4).

   Single-run drill-in surface. Reads:
     - GET /api/data/admin/logos/enrich/{run_id}            (run summary)
     - GET /api/data/admin/logos/enrich/{run_id}/outcomes    (paginated outcomes)
     - POST /api/data/admin/logos/enrich/{run_id}/cohort     (re-cohort builder)
     - GET /api/data/admin/logos/enrich/{run_id}/outcomes.csv (CSV export)

   Composes:
     - Run summary tiles (cohort / processed / domains / logos / errors / collisions)
     - Recovery banner (if recoverable + recovery_cohort_size > 0)
     - Outcome filter chips (drives `category` query param to backend)
     - Search / confidence-range / sort controls
     - Paginated OutcomeTable (shared with Launcher visual semantics)
     - Re-cohort modal (default Dry-run, navigates to Launcher pre-filled)
     - CSV export (filter-aware)

   Operator directive 2026-02-10: re-cohort always suggests Dry-run mode;
   the operator must opt into Live in the Launcher itself. */

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
    ArrowLeft, ArrowsClockwise, CheckCircle, CircleNotch, Clock,
    DownloadSimple, Pause, Plug, WarningCircle, XCircle,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter,
    DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import OutcomeTable from "../../components/data/OutcomeTable";
import LogoReviewTab from "../../components/data/LogoReviewTab";
import { toast } from "sonner";

const PAGE_SIZE = 25;
const TERMINAL_STATUSES = new Set([
    "completed", "failed", "partial", "interrupted", "timed_out",
]);
const POLL_INTERVAL_MS = 5000;

// Category filter chips. The composite "candidate_found" (domain_only +
// third_party) is presented as a separate chip per operator directive
// 2026-02-10 — useful when triaging "discoverable but not committed".
const CATEGORY_CHIPS = [
    { key: "all", label: "All" },
    { key: "wrote", label: "Wrote", color: "bg-emerald-600 text-white border-emerald-600" },
    { key: "would_write", label: "Would write", color: "bg-emerald-500 text-white border-emerald-500" },
    { key: "domain_only", label: "Domain-only", color: "bg-slate-700 text-white border-slate-700" },
    { key: "third_party", label: "3rd-party", color: "bg-rose-600 text-white border-rose-600" },
    { key: "candidate_found", label: "Candidate found", color: "bg-indigo-600 text-white border-indigo-600" },
    { key: "collision", label: "Collisions", color: "bg-amber-500 text-white border-amber-500" },
    { key: "skipped", label: "Skipped", color: "bg-slate-500 text-white border-slate-500" },
    { key: "error", label: "Errors", color: "bg-rose-700 text-white border-rose-700" },
];

const SORT_OPTIONS = [
    { value: "confidence_score_desc", label: "Score · highest first" },
    { value: "confidence_score_asc", label: "Score · lowest first" },
    { value: "company_name_asc", label: "Company name A–Z" },
    { value: "processed_at_desc", label: "Processed · newest first" },
    { value: "processed_at_asc", label: "Processed · oldest first" },
];

function StatusBadge({ status }) {
    const map = {
        queued: { Icon: Clock, cls: "bg-slate-100 text-slate-700", label: "Queued" },
        running: { Icon: CircleNotch, cls: "bg-blue-100 text-blue-700", label: "Running", spin: true },
        completed: { Icon: CheckCircle, cls: "bg-emerald-100 text-emerald-700", label: "Completed" },
        failed: { Icon: XCircle, cls: "bg-rose-100 text-rose-700", label: "Failed" },
        partial: { Icon: Pause, cls: "bg-amber-100 text-amber-800", label: "Partial" },
        interrupted: { Icon: Plug, cls: "bg-amber-100 text-amber-800", label: "Interrupted" },
        timed_out: { Icon: WarningCircle, cls: "bg-amber-100 text-amber-800", label: "Timed out" },
    };
    const entry = map[status] || map.queued;
    const Icon = entry.Icon;
    return (
        <span
            data-testid={`run-detail-status-${status}`}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium ${entry.cls}`}
        >
            <Icon size={12} weight="fill" className={entry.spin ? "animate-spin" : ""} />
            {entry.label}
        </span>
    );
}

function Tile({ label, value, accent, testId }) {
    const rendered = typeof value === "string" ? value : (value || 0).toLocaleString();
    return (
        <div data-testid={testId} className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="reedos-eyebrow text-slate-500">{label}</div>
            <div className={`text-2xl font-display font-bold tabular-nums mt-1.5 ${accent || "text-slate-900"}`}>
                {rendered}
            </div>
        </div>
    );
}

function formatDuration(ms) {
    if (!ms || ms <= 0) return "—";
    const s = Math.round(ms / 1000);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const rs = s % 60;
    if (m < 60) return `${m}m ${rs}s`;
    const h = Math.floor(m / 60);
    const rm = m % 60;
    return `${h}h ${rm}m`;
}

export default function LogoEnrichmentRunDetail() {
    const { runId } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const [run, setRun] = useState(null);
    const [runLoading, setRunLoading] = useState(true);
    const [outcomes, setOutcomes] = useState({ total: 0, page: 1, page_size: PAGE_SIZE, items: [], outcome_counts: {}, persisted: false });
    const [outcomesLoading, setOutcomesLoading] = useState(false);
    // PROP-002 / iter 85.12 — Review-queue tab.
    // The tab strip is added below the run summary; default tab is
    // "review" iff this run has pending reviews, else "outcomes".
    const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "outcomes");
    const [reviewCounts, setReviewCounts] = useState({ pending_review: 0, approved: 0, human_corrected: 0, rejected: 0, skipped: 0, not_eligible: 0, total: 0 });

    const category = searchParams.get("category") || "all";
    const q = searchParams.get("q") || "";
    const minConfidence = searchParams.get("min_confidence") || "";
    const maxConfidence = searchParams.get("max_confidence") || "";
    const sort = searchParams.get("sort") || "confidence_score_desc";
    const page = Math.max(1, parseInt(searchParams.get("page") || "1", 10));

    // Local input draft for `q` so the user can type without re-firing
    // a query on every keystroke. Submit on Enter or when the input
    // loses focus (blur) — same UX pattern as CompanyRepository search.
    const [qDraft, setQDraft] = useState(q);
    useEffect(() => {
        Promise.resolve().then(() => setQDraft(q));
    }, [q]);

    const updateParam = (key, value) => {
        const next = new URLSearchParams(searchParams);
        if (value === "" || value === null || value === undefined) {
            next.delete(key);
        } else {
            next.set(key, value);
        }
        if (key !== "page") next.delete("page");
        setSearchParams(next);
    };

    const outcomesQs = useMemo(() => {
        const p = new URLSearchParams();
        p.set("page", String(page));
        p.set("page_size", String(PAGE_SIZE));
        p.set("sort", sort);
        if (category && category !== "all") p.set("category", category);
        if (q) p.set("q", q);
        if (minConfidence) p.set("min_confidence", minConfidence);
        if (maxConfidence) p.set("max_confidence", maxConfidence);
        return p.toString();
    }, [page, sort, category, q, minConfidence, maxConfidence]);

    // Load the run summary on runId change. Inlined IIFE pattern per the
    // proven `CompanyRepository.jsx` reference — matches React 19's
    // `react-hooks/set-state-in-effect` rule expectations.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            setRunLoading(true);
            try {
                const { data } = await api.get(`/data/admin/logos/enrich/${encodeURIComponent(runId)}`);
                if (cancelled) return;
                setRun(data);
            } catch (err) {
                if (cancelled) return;
                toast.error(formatApiError(err, "Failed to load run"));
            } finally {
                if (!cancelled) setRunLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [runId]);

    // Load outcomes whenever the filter query string changes.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            setOutcomesLoading(true);
            try {
                const { data } = await api.get(`/data/admin/logos/enrich/${encodeURIComponent(runId)}/outcomes?${outcomesQs}`);
                if (cancelled) return;
                setOutcomes(data);
            } catch (err) {
                if (cancelled) return;
                toast.error(formatApiError(err, "Failed to load outcomes"));
            } finally {
                if (!cancelled) setOutcomesLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [runId, outcomesQs]);

    // Poll while non-terminal so the operator sees the run progress.
    useEffect(() => {
        if (!run || TERMINAL_STATUSES.has(run.status)) return undefined;
        const t = setTimeout(() => {
            (async () => {
                try {
                    const { data } = await api.get(`/data/admin/logos/enrich/${encodeURIComponent(runId)}`);
                    setRun(data);
                } catch {
                    // transient — next interval will retry
                }
            })();
        }, POLL_INTERVAL_MS);
        return () => clearTimeout(t);
    }, [run, runId]);

    // PROP-002 / iter 85.12 — fetch the per-run review counts. Drives
    // the tab-strip badges + auto-defaults the active tab to "review"
    // when the operator first lands on a run with pending work.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(`/data/admin/logos/enrich/${encodeURIComponent(runId)}/review-counts`);
                if (cancelled) return;
                setReviewCounts(data || {});
                // Auto-default to Review tab if this run has pending
                // candidates AND the operator did not specify ?tab=.
                if (!searchParams.get("tab") && (data?.pending_review || 0) > 0) {
                    setActiveTab("review");
                }
            } catch {
                // legacy run / endpoint unavailable — keep defaults
            }
        })();
        return () => { cancelled = true; };
    }, [runId, searchParams]);

    // Keep the URL ?tab= in sync with the active tab so a refresh
    // preserves context.
    useEffect(() => {
        const cur = searchParams.get("tab");
        if (cur !== activeTab) {
            const next = new URLSearchParams(searchParams);
            if (activeTab === "outcomes") {
                next.delete("tab");
            } else {
                next.set("tab", activeTab);
            }
            setSearchParams(next, { replace: true });
        }
    }, [activeTab, searchParams, setSearchParams]);

    const downloadCsv = async () => {
        if (!run) return;
        const params = new URLSearchParams();
        if (category && category !== "all") params.set("category", category);
        if (q) params.set("q", q);
        if (minConfidence) params.set("min_confidence", minConfidence);
        if (maxConfidence) params.set("max_confidence", maxConfidence);
        try {
            const resp = await api.get(
                `/data/admin/logos/enrich/${encodeURIComponent(runId)}/outcomes.csv?${params.toString()}`,
                { responseType: "blob" },
            );
            const url = URL.createObjectURL(resp.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `enrichment-${runId}-${category || "all"}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("CSV download started.");
        } catch (err) {
            toast.error(formatApiError(err, "Failed to download CSV"));
        }
    };

    const [recohortOpen, setRecohortOpen] = useState(false);
    // Iter 39 Phase 5 — track which entrypoint opened the modal so we
    // can pre-select the right category. Recovery banner → "recovery";
    // the standard "Re-cohort" header button → the current view's
    // category (or "would_write" if viewing All).
    const [recohortInitialCategory, setRecohortInitialCategory] = useState("would_write");
    const openRecohort = (initial) => {
        setRecohortInitialCategory(initial);
        setRecohortOpen(true);
    };

    const pageCount = Math.max(1, Math.ceil((outcomes.total || 0) / PAGE_SIZE));
    const isTerminal = run && TERMINAL_STATUSES.has(run.status);
    const showRecoveryBanner = run && run.recoverable && (run.recovery_cohort_size || 0) > 0;

    return (
        <div data-testid="logo-enrichment-run-detail-page">
            <PageHeader
                eyebrow="Data · Logo Enrichment"
                title={`Run ${runId}`}
                description="Per-row outcomes, CSV export, and re-cohort builder for this enrichment run."
                actions={(
                    <div className="flex items-center gap-2">
                        <Link to="/data/logo-enrichment/runs">
                            <Button variant="outline" size="sm" className="gap-2" data-testid="back-to-runs">
                                <ArrowLeft size={14} />
                                Back to runs
                            </Button>
                        </Link>
                    </div>
                )}
            />

            <div className="px-6 sm:px-8 py-6 space-y-6 max-w-7xl">

                {/* ── Run summary ───────────────────────────── */}
                {runLoading && (
                    <div className="rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-500" data-testid="run-detail-loading">
                        Loading run…
                    </div>
                )}
                {!runLoading && run && (
                    <section className="rounded-lg border border-slate-200 bg-white p-6" data-testid="run-detail-summary">
                        <div className="flex items-start justify-between gap-4 mb-4">
                            <div>
                                <div className="flex items-center gap-2 mb-1">
                                    <StatusBadge status={run.status} />
                                    <span
                                        data-testid="run-detail-mode"
                                        className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded font-semibold ${run.mode === "live" ? "bg-slate-200 text-slate-700" : "bg-slate-100 text-slate-600"}`}
                                    >
                                        {run.mode === "live" ? "SKIP REVIEW" : "DRY-RUN"}
                                    </span>
                                </div>
                                <div className="font-mono text-sm text-slate-700" data-testid="run-detail-id">{run.run_id}</div>
                                <div className="text-xs text-slate-500 mt-0.5">
                                    Started {run.started_at}
                                    {run.finished_at && <> · Finished {run.finished_at}</>}
                                    {isTerminal && run.duration_ms > 0 && <> · Took {formatDuration(run.duration_ms)}</>}
                                </div>
                                {run.triggered_by_email && (
                                    <div className="text-xs text-slate-500 mt-0.5">
                                        Triggered by <span className="font-mono">{run.triggered_by_email}</span>
                                    </div>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={downloadCsv}
                                    disabled={!run}
                                    className="gap-2"
                                    data-testid="download-csv-btn"
                                >
                                    <DownloadSimple size={14} />
                                    Export CSV
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => openRecohort(category === "all" ? "would_write" : category)}
                                    disabled={!run || !isTerminal}
                                    className="gap-2"
                                    data-testid="open-recohort-btn"
                                >
                                    <ArrowsClockwise size={14} />
                                    Re-cohort
                                </Button>
                            </div>
                        </div>

                        {run.error_message && (
                            <div className="mb-4 p-3 rounded border border-rose-200 bg-rose-50 text-sm text-rose-700" data-testid="run-detail-error-banner">
                                <strong>Run failed:</strong> {run.error_message}
                            </div>
                        )}

                        {showRecoveryBanner && (
                            <div className="mb-4 p-3 rounded border border-indigo-200 bg-indigo-50 text-sm" data-testid="recovery-banner">
                                <div className="flex items-center gap-2 text-indigo-800 font-semibold">
                                    <ArrowsClockwise size={14} weight="bold" />
                                    Recoverable — {(run.recovery_cohort_size || 0).toLocaleString()} row{(run.recovery_cohort_size || 0) === 1 ? "" : "s"} unprocessed
                                </div>
                                <div className="text-xs text-indigo-700 mt-1">
                                    This run did not complete its full cohort. Build a recovery cohort below or click <strong>Re-cohort</strong> to re-feed the unprocessed rows into the launcher (defaults to Dry-run).
                                </div>
                                <div className="mt-2">
                                    <Button
                                        size="sm"
                                        variant="default"
                                        data-testid="recover-now-btn"
                                        onClick={() => openRecohort("recovery")}
                                        className="bg-indigo-600 hover:bg-indigo-700 gap-2"
                                    >
                                        <ArrowsClockwise size={12} weight="bold" />
                                        Build recovery cohort
                                    </Button>
                                </div>
                            </div>
                        )}

                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
                            <Tile label="Cohort" value={run.cohort_total} testId="rd-tile-cohort" />
                            <Tile label="Scanned" value={run.rows_scanned} testId="rd-tile-scanned" />
                            <Tile label="Processed" value={run.rows_processed} accent="text-slate-700" testId="rd-tile-processed" />
                            <Tile label="Domains" value={run.domains_discovered} accent="text-blue-700" testId="rd-tile-domains" />
                            <Tile label={run.mode === "live" ? "Logos written" : "Logos resolvable"} value={run.mode === "live" ? run.rows_wrote_mongo : run.logos_acquired} accent="text-emerald-700" testId="rd-tile-logos" />
                            <Tile label="Collisions" value={run.rows_skipped_collision} accent="text-amber-700" testId="rd-tile-collisions" />
                            <Tile label="Errors" value={run.errors_count} accent={run.errors_count > 0 ? "text-rose-700" : undefined} testId="rd-tile-errors" />
                        </div>

                        {/* Iter 87 — Review-state summary strip (visible without opening the Review tab) */}
                        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="rd-review-summary">
                            <Tile label="Pending Review" value={reviewCounts.pending_review} accent="text-amber-700" testId="rd-tile-pending" />
                            <Tile label="Approved" value={reviewCounts.approved} accent="text-emerald-700" testId="rd-tile-approved" />
                            <Tile label="Human Corrected" value={reviewCounts.human_corrected} accent="text-cyan-700" testId="rd-tile-human-corrected" />
                            <Tile label="Rejected" value={reviewCounts.rejected} accent="text-rose-700" testId="rd-tile-rejected" />
                            <Tile
                                label="AI Accuracy"
                                value={(() => {
                                    const decided = (reviewCounts.approved || 0) + (reviewCounts.human_corrected || 0);
                                    if (!decided) return "—";
                                    const pct = ((reviewCounts.approved || 0) * 100) / decided;
                                    return `${pct.toFixed(1)}%`;
                                })()}
                                accent="text-indigo-700"
                                testId="rd-tile-ai-accuracy"
                            />
                        </div>
                    </section>
                )}

                {/* ── Outcome filter chips + counts ────────── */}
                {/* ── PROP-002 / iter 85.12 · Tab strip ─────────────── */}
                {!runLoading && run && (
                    <nav className="flex items-center gap-1 border-b border-slate-200" role="tablist" data-testid="run-detail-tabs">
                        <TabBtn
                            label="Outcomes"
                            active={activeTab === "outcomes"}
                            onClick={() => setActiveTab("outcomes")}
                            count={outcomes.total}
                            testid="tab-outcomes"
                        />
                        <TabBtn
                            label="Review"
                            active={activeTab === "review"}
                            onClick={() => setActiveTab("review")}
                            count={reviewCounts.pending_review || 0}
                            countTone={reviewCounts.pending_review ? "amber" : "slate"}
                            testid="tab-review"
                        />
                    </nav>
                )}

                {/* ── PROP-002 / iter 85.12 · Review tab body ───────── */}
                {!runLoading && run && activeTab === "review" && (
                    <section data-testid="run-detail-review-section">
                        <LogoReviewTab runId={runId} />
                    </section>
                )}

                {/* ── Outcome filters (legacy "Outcomes" tab) ────── */}
                {!runLoading && run && activeTab === "outcomes" && (
                    <section className="rounded-lg border border-slate-200 bg-white p-4" data-testid="outcome-filter-section">
                        <div className="flex items-center justify-between mb-3">
                            <div className="text-sm text-slate-700 font-medium">
                                Outcomes
                                {outcomes.persisted ? (
                                    <span className="ml-2 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">persisted</span>
                                ) : (
                                    <span
                                        className="ml-2 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-100 text-slate-600"
                                        title="Legacy run — outcomes inferred from the in-document sample (capped at 500). Newer runs persist every outcome."
                                    >
                                        sampled
                                    </span>
                                )}
                            </div>
                            <div className="text-xs text-slate-500" data-testid="outcomes-total">
                                {(outcomes.total || 0).toLocaleString()} matching outcome{(outcomes.total || 0) === 1 ? "" : "s"}
                            </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2" data-testid="outcome-chip-row">
                            {CATEGORY_CHIPS.map((c) => {
                                const active = category === c.key;
                                const count = c.key === "all"
                                    ? Object.values(outcomes.outcome_counts || {}).reduce((a, b) => a + b, 0)
                                    : c.key === "candidate_found"
                                        ? (outcomes.outcome_counts?.domain_only || 0) + (outcomes.outcome_counts?.third_party || 0)
                                        : (outcomes.outcome_counts?.[c.key] || 0);
                                return (
                                    <button
                                        key={c.key}
                                        type="button"
                                        data-testid={`outcome-chip-${c.key}`}
                                        onClick={() => updateParam("category", c.key === "all" ? "" : c.key)}
                                        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs transition-colors ${
                                            active
                                                ? (c.color || "bg-slate-900 text-white border-slate-900")
                                                : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                                        }`}
                                    >
                                        <span>{c.label}</span>
                                        <span className={`tabular-nums font-mono text-[11px] ${active ? "opacity-80" : "text-slate-400"}`}>{count}</span>
                                    </button>
                                );
                            })}
                        </div>

                        {/* Search + confidence + sort */}
                        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                            <div>
                                <label className="block reedos-eyebrow text-slate-500 mb-1.5">Search</label>
                                <Input
                                    type="text"
                                    placeholder="company / symbol / domain"
                                    data-testid="outcome-search"
                                    value={qDraft}
                                    onChange={(e) => setQDraft(e.target.value)}
                                    onBlur={() => { if (qDraft !== q) updateParam("q", qDraft.trim()); }}
                                    onKeyDown={(e) => { if (e.key === "Enter") updateParam("q", qDraft.trim()); }}
                                    className="h-9 text-sm"
                                />
                            </div>
                            <div>
                                <label className="block reedos-eyebrow text-slate-500 mb-1.5">Min score</label>
                                <Input
                                    type="number"
                                    step="0.05"
                                    min="0"
                                    max="1"
                                    placeholder="0.00"
                                    data-testid="outcome-min-confidence"
                                    value={minConfidence}
                                    onChange={(e) => updateParam("min_confidence", e.target.value)}
                                    className="h-9 text-sm"
                                />
                            </div>
                            <div>
                                <label className="block reedos-eyebrow text-slate-500 mb-1.5">Max score</label>
                                <Input
                                    type="number"
                                    step="0.05"
                                    min="0"
                                    max="1"
                                    placeholder="1.00"
                                    data-testid="outcome-max-confidence"
                                    value={maxConfidence}
                                    onChange={(e) => updateParam("max_confidence", e.target.value)}
                                    className="h-9 text-sm"
                                />
                            </div>
                            <div>
                                <label className="block reedos-eyebrow text-slate-500 mb-1.5">Sort</label>
                                <select
                                    data-testid="outcome-sort"
                                    value={sort}
                                    onChange={(e) => updateParam("sort", e.target.value)}
                                    className="w-full h-9 text-sm rounded-md border border-slate-200 bg-white px-2"
                                >
                                    {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                            </div>
                        </div>
                    </section>
                )}

                {/* ── Outcome table + pagination (legacy "Outcomes" tab) ── */}
                {!runLoading && run && activeTab === "outcomes" && (
                    <section className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="run-detail-outcomes-section">
                        {outcomesLoading && (
                            <div className="px-4 py-3 border-b border-slate-100 text-xs text-slate-500">Loading outcomes…</div>
                        )}
                        <OutcomeTable
                            outcomes={outcomes.items}
                            emptyLabel="No outcomes match the current filters for this run."
                        />
                        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-xs text-slate-500">
                            <span data-testid="outcomes-pagination-summary">
                                Page {page} of {pageCount} · {(outcomes.total || 0).toLocaleString()} total
                            </span>
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    data-testid="outcomes-page-prev"
                                    disabled={page <= 1 || outcomesLoading}
                                    onClick={() => updateParam("page", String(page - 1))}
                                >Prev</Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    data-testid="outcomes-page-next"
                                    disabled={page >= pageCount || outcomesLoading}
                                    onClick={() => updateParam("page", String(page + 1))}
                                >Next</Button>
                            </div>
                        </div>
                    </section>
                )}
            </div>

            {/* ── Re-cohort modal ───────────────────────────── */}
            <RecohortDialog
                open={recohortOpen}
                onOpenChange={setRecohortOpen}
                runId={runId}
                initialCategory={recohortInitialCategory}
                outcomeCounts={outcomes.outcome_counts || {}}
                recoveryCohortSize={run?.recovery_cohort_size || 0}
                onLaunch={(payload) => {
                    setRecohortOpen(false);
                    // Navigate to the Launcher with the cohort pre-filled
                    // via React Router state. Suggested mode is always
                    // dry_run per operator directive.
                    navigate("/data/logo-enrichment", { state: { prefillCohort: payload } });
                }}
            />
        </div>
    );
}

// ────────────────────────────────────────────────────────────────────
// PROP-002 / iter 85.12 — Tab button helper.
// ────────────────────────────────────────────────────────────────────
function TabBtn({ label, active, onClick, count, countTone = "slate", testid }) {
    const countMap = {
        slate: "bg-slate-100 text-slate-600",
        amber: "bg-amber-100 text-amber-800",
        emerald: "bg-emerald-100 text-emerald-800",
    };
    return (
        <button
            type="button"
            role="tab"
            aria-selected={active}
            onClick={onClick}
            data-testid={testid}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors inline-flex items-center gap-2 ${
                active
                    ? "border-cyan-600 text-cyan-800"
                    : "border-transparent text-slate-500 hover:text-slate-900 hover:bg-slate-50"
            }`}
        >
            {label}
            {typeof count === "number" && count > 0 && (
                <span className={`text-[10px] tabular-nums font-mono px-1.5 py-0.5 rounded ${countMap[countTone] || countMap.slate}`}>
                    {count}
                </span>
            )}
        </button>
    );
}

// ────────────────────────────────────────────────────────────────────
// Re-cohort dialog — calls POST /enrich/{run_id}/cohort and hands the
// resulting company_ids to the Launcher via navigation state.
// ────────────────────────────────────────────────────────────────────
function RecohortDialog({ open, onOpenChange, runId, initialCategory, outcomeCounts, recoveryCohortSize, onLaunch }) {
    const RECOHORT_CATEGORY_OPTIONS = [
        // Iter 39 Phase 5 — Recovery is the natural follow-up for any
        // non-completed terminal run. Listed first so it surfaces above
        // every outcome-derived category.
        { value: "recovery", label: "Recovery — unprocessed rows" },
        { value: "wrote", label: "Wrote (re-attempt writes)" },
        { value: "would_write", label: "Would write (commit-eligible)" },
        { value: "domain_only", label: "Domain-only" },
        { value: "third_party", label: "3rd-party (suppressed)" },
        { value: "candidate_found", label: "Candidate found (domain_only + 3rd-party)" },
        { value: "collision", label: "Collisions" },
        { value: "skipped", label: "Skipped" },
        { value: "error", label: "Errors" },
        { value: "unresolved", label: "Unresolved" },
    ];

    const [cat, setCat] = useState(initialCategory || "would_write");
    const [limit, setLimit] = useState(500);
    const [busy, setBusy] = useState(false);
    const [preview, setPreview] = useState(null);

    useEffect(() => {
        if (!open) return;
        Promise.resolve().then(() => {
            setCat(initialCategory || "would_write");
            setLimit(500);
            setPreview(null);
        });
    }, [open, initialCategory]);

    const submit = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(
                `/data/admin/logos/enrich/${encodeURIComponent(runId)}/cohort`,
                { category: cat, limit: Math.max(1, Math.min(2000, parseInt(limit, 10) || 500)) },
            );
            setPreview(data);
        } catch (err) {
            toast.error(formatApiError(err, "Re-cohort failed"));
        } finally {
            setBusy(false);
        }
    };

    const handleLaunch = () => {
        if (!preview) return;
        onLaunch({
            source_run_id: preview.source_run_id,
            category: preview.category,
            company_ids: preview.company_ids,
            suggested_mode: preview.suggested_mode,
        });
    };

    const expectedCount = cat === "recovery"
        ? (recoveryCohortSize || 0)
        : cat === "all"
            ? Object.values(outcomeCounts).reduce((a, b) => a + b, 0)
            : cat === "candidate_found"
                ? (outcomeCounts.domain_only || 0) + (outcomeCounts.third_party || 0)
                : (outcomeCounts[cat] || 0);

    const expectedCountHint = cat === "recovery"
        ? `${expectedCount.toLocaleString()} row${expectedCount === 1 ? "" : "s"} from this run's targeted cohort produced no outcome record (engine interrupted, timed out, or partial). They will re-feed into the launcher.`
        : `Approx. ${expectedCount.toLocaleString()} outcome${expectedCount === 1 ? "" : "s"} match this category in the visible page counts.`;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="recohort-dialog" className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>Re-cohort from {runId}</DialogTitle>
                    <DialogDescription>
                        Materialise a new cohort from this run&apos;s outcomes. The Launcher will receive the company IDs pre-filled — you still choose <strong>mode</strong> and confirm. Default suggested mode is <span className="font-mono">dry_run</span>.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 mt-2">
                    <div>
                        <label className="block reedos-eyebrow text-slate-500 mb-1.5">Outcome category</label>
                        <select
                            data-testid="recohort-category"
                            value={cat}
                            onChange={(e) => setCat(e.target.value)}
                            className="w-full h-9 text-sm rounded-md border border-slate-200 bg-white px-2"
                        >
                            {RECOHORT_CATEGORY_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                        <div className="text-[11px] text-slate-400 mt-1" data-testid="recohort-expected-hint">
                            {expectedCountHint}
                        </div>
                    </div>
                    <div>
                        <label className="block reedos-eyebrow text-slate-500 mb-1.5">Limit (max 2000)</label>
                        <Input
                            type="number"
                            min={1}
                            max={2000}
                            value={limit}
                            onChange={(e) => setLimit(e.target.value)}
                            className="h-9 text-sm w-40"
                            data-testid="recohort-limit"
                        />
                    </div>

                    {preview && (
                        <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm" data-testid="recohort-preview">
                            <div className="font-medium text-slate-700 mb-1">
                                Cohort built: {preview.cohort_size.toLocaleString()} compan{preview.cohort_size === 1 ? "y" : "ies"}
                                {preview.truncated && <span className="ml-1 text-amber-700 text-xs">(truncated at {limit})</span>}
                            </div>
                            <div className="text-xs text-slate-500">
                                Suggested mode: <span className="font-mono">{preview.suggested_mode}</span>
                            </div>
                            {preview.company_ids.length > 0 && (
                                <div className="mt-2 text-[11px] text-slate-500 font-mono break-all max-h-24 overflow-y-auto">
                                    {preview.company_ids.slice(0, 12).join(", ")}
                                    {preview.company_ids.length > 12 && (
                                        <> … and {preview.company_ids.length - 12} more</>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <DialogFooter className="mt-4">
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={busy}
                        data-testid="recohort-cancel"
                    >
                        Cancel
                    </Button>
                    {!preview ? (
                        <Button
                            onClick={submit}
                            disabled={busy}
                            data-testid="recohort-build"
                        >
                            {busy ? "Building…" : "Build cohort"}
                        </Button>
                    ) : (
                        <Button
                            onClick={handleLaunch}
                            disabled={preview.cohort_size === 0}
                            data-testid="recohort-launch"
                            className="gap-2"
                        >
                            <ArrowsClockwise size={14} />
                            Open in Launcher (Dry-run)
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
